import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export default function StickyScanBar() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const hero = document.querySelector('[data-testid="hero-section"]') || document.querySelector('[data-testid="profession-hero"]');
    const midPage = document.querySelector('[data-testid="mid-page-close"]');
    const finalCta = document.querySelector('[data-testid="final-cta-section"]') || document.querySelector('[data-testid="profession-final-cta"]');
    const footer = document.querySelector('[data-testid="landing-footer"]');

    if (!hero) return;

    const heroState = { visible: true };
    const midState = { visible: false };
    const finalState = { visible: false };
    const footerState = { visible: false };

    const update = () => {
      const shouldHide =
        heroState.visible ||
        midState.visible ||
        finalState.visible ||
        footerState.visible;
      setVisible(!shouldHide);
    };

    const observers = [];

    // Hero observer
    const heroObs = new IntersectionObserver(
      ([entry]) => { heroState.visible = entry.isIntersecting; update(); },
      { threshold: 0.1 }
    );
    heroObs.observe(hero);
    observers.push(heroObs);

    // Mid-page observer
    if (midPage) {
      const midObs = new IntersectionObserver(
        ([entry]) => { midState.visible = entry.isIntersecting; update(); },
        { threshold: 0.2 }
      );
      midObs.observe(midPage);
      observers.push(midObs);
    }

    // Final CTA observer
    if (finalCta) {
      const finalObs = new IntersectionObserver(
        ([entry]) => { finalState.visible = entry.isIntersecting; update(); },
        { threshold: 0.2 }
      );
      finalObs.observe(finalCta);
      observers.push(finalObs);
    }

    // Footer observer
    if (footer) {
      const footerObs = new IntersectionObserver(
        ([entry]) => { footerState.visible = entry.isIntersecting; update(); },
        { threshold: 0.05 }
      );
      footerObs.observe(footer);
      observers.push(footerObs);
    }

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const handleScan = () => {
    if (scanInput.trim()) {
      navigate('/onboarding', { state: { businessName: scanInput.trim() } });
    } else {
      // Shake input if empty
      if (inputRef.current) {
        inputRef.current.style.animation = 'none';
        void inputRef.current.offsetWidth; // reflow
        inputRef.current.style.animation = 'sticky-shake 0.2s ease';
      }
      inputRef.current?.focus();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-40"
          style={{
            height: '64px',
            backgroundColor: 'rgba(44,37,32,0.97)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            borderTop: '1px solid rgba(240,235,227,0.08)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          data-testid="sticky-scan-bar"
        >
          <div
            className="h-full flex items-center gap-4 mx-auto px-4 md:px-6"
            style={{ maxWidth: '800px' }}
          >
            {/* Micro headline — hidden on mobile */}
            <span
              className="hidden md:block flex-shrink-0"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                color: 'rgba(240,235,227,0.7)',
                whiteSpace: 'nowrap',
              }}
              data-testid="sticky-bar-headline"
            >
              Find out if AI mentions you
            </span>

            {/* Input */}
            <input
              ref={inputRef}
              type="text"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleScan()}
              placeholder="Business name or website"
              className="flex-1 min-w-[160px] h-10 px-3 text-[13px] outline-none"
              style={{
                backgroundColor: 'rgba(240,235,227,0.08)',
                border: '1px solid rgba(240,235,227,0.12)',
                borderRadius: 0,
                color: '#F0EBE3',
                fontFamily: "'DM Sans', sans-serif",
              }}
              data-testid="sticky-bar-input"
            />

            {/* Scan button */}
            <button
              onClick={handleScan}
              className="flex-shrink-0 h-10 px-6 text-[12px] font-medium transition-all hover:brightness-110"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                backgroundColor: '#B8985A',
                color: '#F0EBE3',
                borderRadius: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
              data-testid="sticky-bar-scan-btn"
            >
              Scan now
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
